import React, { useState, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { makeRequest2 } from "../../../utils/makeRequest";
import { getItem } from "../../../utils/localStorage";
import { toastify } from "../../../utils/toast";
import { getBookingBlockedDatesURL } from "../../../utils/urls";

const BookingCalendar = ({
    bookingPropertyId,
    selectedDates = [],
    onDateChange,
    minDate = null,
    maxDate = null,
    readOnly = false,
    showBlockedReason = false
}) => {
    const [loading, setLoading] = useState(true);
    const [blockedDates, setBlockedDates] = useState([]);
    const [facilityId, setFacilityId] = useState(null);
    const [error, setError] = useState(null);

    // Initialize component and load facility ID
    useEffect(() => {
        const loadFacilityId = async () => {
            try {
                const facilityId = await getItem("selectedFacilityId");
                if (!facilityId) {
                    console.error("No facility ID available");
                    setError("No facility selected");
                    return;
                }
                setFacilityId(facilityId);
                return facilityId;
            } catch (error) {
                console.error("Error loading facility ID:", error);
                setError("Error loading facility data");
                return null;
            }
        };

        loadFacilityId().then(id => {
            if (id) {
                fetchBlockedDates(id);
            }
        });
    }, []);

    // Fetch blocked dates when property ID changes
    useEffect(() => {
        if (facilityId && bookingPropertyId) {
            console.log(`Fetching dates for specific property: ${bookingPropertyId}`);
            fetchBlockedDates(facilityId);
        } else if (facilityId) {
            console.warn('No booking property ID provided, calendar will not show unavailable dates');
            setBlockedDates([]);
            setLoading(false);
        }
    }, [facilityId, bookingPropertyId]);

    // Fetch blocked and reserved dates from API
    const fetchBlockedDates = async (facilityId) => {
        if (!bookingPropertyId) {
            console.log("No booking property ID available, skipping fetch");
            setBlockedDates([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            console.log(`Fetching blocked dates for property ID: ${bookingPropertyId}`);

            // Use the URL constant from urls.js
            const url = `${getBookingBlockedDatesURL}/${facilityId}/${bookingPropertyId}`;
            console.log(`API URL: ${url}`);

            const response = await makeRequest2(url, "GET");
            console.log('API response:', response);

            if (response.success) {
                // Get all unavailable dates from the response
                let unavailableDates = response.data?.allUnavailableDates ||
                    response.data?.data?.allUnavailableDates || [];

                console.log(`Received ${unavailableDates.length} unavailable dates for property ${bookingPropertyId}`);

                // Log counts by type
                const blockedCount = unavailableDates.filter(d => d.type === 'blocked').length;
                const reservedCount = unavailableDates.filter(d => d.type === 'reservation').length;
                console.log(`Date counts by type: Blocked=${blockedCount}, Reserved=${reservedCount}`);

                // Ensure dates are properly normalized to Date objects
                const formattedDates = unavailableDates.map(date => ({
                    ...date,
                    startDate: new Date(date.startDate),
                    endDate: new Date(date.endDate)
                }));

                setBlockedDates(formattedDates);
            } else {
                console.error("Error fetching blocked dates:", response.error);
                setError(response.error || "Failed to load blocked dates");
                setBlockedDates([]);
            }
        } catch (error) {
            console.error("Error fetching blocked dates:", error);
            setError("Failed to load blocked dates");
            setBlockedDates([]);
        } finally {
            setLoading(false);
        }
    };

    const ensureCorrectDate = (date) => {
        if (!date) return null;

        // IMPORTANT: Create a fresh date object WITHOUT modifying it
        // This preserves the exact date as selected in the UI
        const newDate = new Date(date);

        // Log the original date for debugging
        console.log('Original date before normalization:', {
            date: newDate.toString(),
            year: newDate.getFullYear(),
            month: newDate.getMonth(), // 0-11
            day: newDate.getDate(),
            hours: newDate.getHours(),
            minutes: newDate.getMinutes()
        });

        // CRITICAL FIX: Get components but DO NOT modify the date
        // Just create a new date object with the EXACT same year/month/day
        // This avoids timezone issues causing month shifts
        const year = newDate.getFullYear();
        const month = newDate.getMonth(); // Keep month as-is (0-11)
        const day = newDate.getDate();

        // Create a clean date with the exact components (midnight)
        const correctedDate = new Date(year, month, day, 0, 0, 0, 0);

        // Log the corrected date for verification
        console.log('Corrected date after normalization:', {
            date: correctedDate.toString(),
            year: correctedDate.getFullYear(),
            month: correctedDate.getMonth(),
            day: correctedDate.getDate()
        });

        return correctedDate;
    };

    // Check if a date is selected
    const isDateSelected = (date) => {
        if (!date || !selectedDates || selectedDates.length === 0) return false;

        // Normalize date by setting time to midnight
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);

        return selectedDates.some(selectedDate => {
            if (!selectedDate) return false;
            const normalizedSelectedDate = new Date(selectedDate);
            normalizedSelectedDate.setHours(0, 0, 0, 0);
            return normalizedSelectedDate.getTime() === normalizedDate.getTime();
        });
    };

    // Check if a date is in a selected range
    const isDateInRange = (date) => {
        if (!date || !selectedDates || selectedDates.length !== 2 || !selectedDates[0] || !selectedDates[1]) {
            return false;
        }

        // Normalize dates by setting time to midnight
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        const dateTime = normalizedDate.getTime();

        const normalizedStart = new Date(selectedDates[0]);
        normalizedStart.setHours(0, 0, 0, 0);
        const startTime = normalizedStart.getTime();

        const normalizedEnd = new Date(selectedDates[1]);
        normalizedEnd.setHours(0, 0, 0, 0);
        const endTime = normalizedEnd.getTime();

        return dateTime > startTime && dateTime < endTime;
    };

    // Check if date is blocked
    const isDateBlocked = (date) => {
        if (!date || !blockedDates || blockedDates.length === 0) return false;

        // Normalize date by setting time to midnight
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        const dateTimestamp = normalizedDate.getTime();

        return blockedDates.some(blockedPeriod => {
            // Convert and normalize blocked date ranges
            const startDate = new Date(blockedPeriod.startDate);
            startDate.setHours(0, 0, 0, 0);
            const startTimestamp = startDate.getTime();

            const endDate = new Date(blockedPeriod.endDate);
            endDate.setHours(0, 0, 0, 0);
            const endTimestamp = endDate.getTime();

            // Check if this date falls within the blocked period
            return dateTimestamp >= startTimestamp && dateTimestamp <= endTimestamp;
        });
    };

    // Find matching blocked date if any
    const getBlockedDateInfo = (date) => {
        if (!date || !blockedDates || blockedDates.length === 0) return null;

        // Normalize date by setting time to midnight
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        const dateTimestamp = normalizedDate.getTime();

        return blockedDates.find(blockedPeriod => {
            // Convert and normalize blocked date ranges properly
            const startDate = new Date(blockedPeriod.startDate);
            startDate.setHours(0, 0, 0, 0);
            const startTimestamp = startDate.getTime();

            const endDate = new Date(blockedPeriod.endDate);
            endDate.setHours(0, 0, 0, 0);
            const endTimestamp = endDate.getTime();

            // Check if this date falls within the blocked period
            return dateTimestamp >= startTimestamp && dateTimestamp <= endTimestamp;
        });
    };

    // Date template to customize each date cell
    const dateTemplate = (dateObj) => {
        if (!dateObj) return null;

        try {
            // Create a Date object from the dateObj (PrimeReact dateObj format)
            const year = dateObj.year;
            const month = dateObj.month - 0; // Convert to 0-based month
            const day = dateObj.day;

            // Create date for comparison
            const date = new Date(year, month, day);
            date.setHours(0, 0, 0, 0); // Normalize time part

            // Check various states of this date
            const selected = isDateSelected(date);
            const inRange = isDateInRange(date);
            const blocked = isDateBlocked(date);
            const blockedInfo = getBlockedDateInfo(date);

            // Set default styles
            let backgroundColor = 'transparent';
            let textColor = 'inherit';
            let borderRadius = selected ? '50%' : 'none';
            let title = '';

            // Apply appropriate styling based on date state
            if (selected) {
                backgroundColor = '#0D6EFD'; // Blue for selected date
                textColor = '#ffffff'; // White text for selected date
                title = 'Selected';
            }
            else if (inRange) {
                backgroundColor = '#E6F0FF';
                textColor = '#212529';
                title = 'In selected range';
            }

            // If date is blocked, override the styles
            if (blocked) {
                if (blockedInfo?.type === 'reservation') {
                    backgroundColor = '#E6F2FF';  // Light blue for reservations
                    textColor = '#0056b3';        // Darker blue text for better readability
                    title = `Reserved ${blockedInfo.reservation ? `by ${blockedInfo.reservation.guestName}` : ''}`;
                } else {
                    // Blocked dates are now a light red/pink
                    backgroundColor = '#FFE6E6';  // Light red/pink for blocked dates
                    textColor = '#721c24';        // Dark red text for better readability
                    title = blockedInfo?.reason || 'Blocked';
                    if (blockedInfo?.notes && showBlockedReason) {
                        title += `: ${blockedInfo.notes}`;
                    }
                }
            }

            return (
                <div
                    className="custom-calendar-day"
                    style={{
                        backgroundColor,
                        color: textColor,
                        borderRadius,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        cursor: blocked ? 'not-allowed' : 'pointer',
                        position: 'relative'
                    }}
                    title={title}
                >
                    {dateObj.day}
                    {blocked && (
                        <span
                            className={`pi ${blockedInfo?.type === 'reservation' ? 'pi-user' : 'pi-lock'}`}
                            style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                fontSize: '0.5rem',
                                color: blockedInfo?.type === 'reservation' ? '#dc3545' : '#6c757d'
                            }}
                        />
                    )}
                </div>
            );
        } catch (err) {
            console.error("Error in dateTemplate:", err, dateObj);
            return dateObj.day;
        }
    };

    // Handle date change - significantly improved
    const handleDateChange = (e) => {
        console.log("Calendar date change:", e);
        console.log("Original event value:", e.value);
        console.log("Selected dates before change:", selectedDates);

        // Handle empty selection case
        if (!e.value || (Array.isArray(e.value) && e.value.length === 0)) {
            console.log("Dates cleared, resetting selection");
            if (onDateChange) {
                onDateChange({
                    ...e,
                    value: []
                });
            }
            return;
        }

        // Process the date selection with our fixed function
        let newValue = null;

        if (Array.isArray(e.value)) {
            // Handle range selection (array of dates)
            newValue = e.value
                .filter(date => date !== null && date !== undefined)
                .map(date => ensureCorrectDate(date));

            // Sort dates chronologically if there are two dates
            if (newValue.length === 2 && newValue[0] && newValue[1]) {
                if (newValue[0] > newValue[1]) {
                    [newValue[0], newValue[1]] = [newValue[1], newValue[0]];
                    console.log("Dates were in reverse order, swapping them");
                }
            }
        } else {
            // Handle single date selection
            newValue = ensureCorrectDate(e.value);
        }

        console.log("Final processed dates:", newValue);

        // Validate dates aren't blocked
        if (Array.isArray(newValue)) {
            // Check if any selected date is blocked
            const hasBlockedDate = newValue.some(date => {
                if (!date) return false;
                return isDateBlocked(date);
            });

            if (hasBlockedDate) {
                toastify("Cannot select blocked or reserved dates", "warning");
                return; // Don't update the selection
            }

            // If selecting a range, check if any date in the range is blocked
            if (newValue.length === 2 && newValue[0] && newValue[1]) {
                // Create a copy of the start date to iterate through the range
                const start = new Date(newValue[0]);
                const end = new Date(newValue[1]);

                // Check each date in the range for blocks
                let currentDate = new Date(start);
                while (currentDate <= end) {
                    if (isDateBlocked(new Date(currentDate))) {
                        toastify("Cannot select a range containing blocked or reserved dates", "warning");
                        return; // Don't update the selection
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            }
        }

        // All validation passed, update the selection
        if (onDateChange) {
            onDateChange({
                ...e,
                value: newValue
            });
        }
    };

    // Disable blocked dates
    const disabledDates = (date) => {
        if (!date) return false;
        // Normalize date for comparison
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        // Check if this date is blocked
        return isDateBlocked(normalizedDate);
    };

    return (
        <div className="booking-calendar">
            {error && (
                <div className="p-2 mb-2 text-sm text-danger">
                    <i className="pi pi-exclamation-circle mr-2"></i>
                    {error}
                </div>
            )}

            {loading ? (
                <div className="booking-calendar-container">
                    <div className="calendar-loading-overlay">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading calendar data...</span>
                        </div>
                        <p className="text-sm">Loading calendar data...</p>
                    </div>
                    {/* Render empty calendar while loading */}
                    <Calendar
                        inline={true}
                        disabled={true}
                    />
                </div>
            ) : (
                <Calendar
                    value={selectedDates}
                    onChange={handleDateChange}
                    selectionMode="range"
                    readOnlyInput={readOnly}
                    inline={true}
                    dateTemplate={dateTemplate}
                    minDate={minDate}
                    maxDate={maxDate}
                    showButtonBar={true}
                    disabled={loading}
                    showClear={true}
                    onClearButtonClick={(e) => {
                        console.log("Clear button clicked");
                        if (onDateChange) {
                            onDateChange({
                                originalEvent: e,
                                value: [],
                                target: { name: null, id: null, value: [] }
                            });
                        }
                    }}
                />
            )}

            {/* Legend */}
            <div className="calendar-legend mt-2 d-flex flex-wrap gap-3">
                <div className="legend-item d-flex align-items-center">
                    <span className="legend-color" style={{ width: '12px', height: '12px', display: 'inline-block', backgroundColor: '#0D6EFD', borderRadius: '50%', marginRight: '5px' }}></span>
                    <small>Selected</small>
                </div>
                <div className="legend-item d-flex align-items-center">
                    <span className="legend-color" style={{ width: '12px', height: '12px', display: 'inline-block', backgroundColor: '#E6F0FF', marginRight: '5px' }}></span>
                    <small>Selected Range</small>
                </div>
                <div className="legend-item d-flex align-items-center">
        <span className="legend-color" style={{ 
            width: '12px', 
            height: '12px', 
            display: 'inline-block', 
            backgroundColor: '#E6F2FF',  // Light blue for reservations 
            marginRight: '5px' 
        }}></span>
        <small>Reserved</small>
    </div>
    <div className="legend-item d-flex align-items-center">
        <span className="legend-color" style={{ 
            width: '12px', 
            height: '12px', 
            display: 'inline-block', 
            backgroundColor: '#FFE6E6',  // Light red/pink for blocked dates
            marginRight: '5px' 
        }}></span>
        <small>Blocked</small>
                </div>
            </div>
        </div>
    );
};

export default BookingCalendar;